import { AlertTriangle, ArrowRightLeft, CalendarOff, Printer, ShieldCheck } from 'lucide-react';
import { CAPACITY_STATUS_LABEL } from '@obraplan/core/domain/planning/capacity-calculator';
import type { DailySchedule } from '@obraplan/core/domain/planning/daily-schedule';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { EmptyState } from '@/components/feedback/states';
import { cn } from '@/lib/cn';

/**
 * A folha do dia.
 *
 * Desenhada para ser **lida em pé, no canteiro** — e impressa. Por isso é uma lista por
 * pessoa, agrupada por equipe, e não uma grade: o encarregado procura a turma dele, lê os
 * nomes e as frentes, e não precisa cruzar linha com coluna segurando o tablet.
 *
 * A mesma marcação de excesso do quadro semanal (cor **e** texto **e** ícone), porque a
 * pessoa que lê esta folha é a que vai executar as horas.
 */
export interface DailyScheduleSheetProps {
  schedule: DailySchedule;
  readOnly: boolean;
  /** Ausente = mover está indisponível (semana fechada ou sem permissão). */
  onMove?: (input: { allocationId: string; version: number; resourceName: string }) => void;
  /** Imprime a ficha daquele funcionário — o que ele vai fazer hoje, para entregar na mão. */
  onPrintSlip: (resourceId: string) => void;
}

export function DailyScheduleSheet({
  schedule,
  readOnly,
  onMove,
  onPrintSlip,
}: DailyScheduleSheetProps) {
  if (schedule.rows.length === 0) {
    return (
      <EmptyState
        title={schedule.isWorkday ? 'Nada programado para este dia' : 'O canteiro não abre hoje'}
        description={
          schedule.isWorkday
            ? 'Nenhuma hora foi alocada nesta data. O planejamento da semana é onde se lança.'
            : 'Feriado ou dia não útil pelo calendário da obra. Alocar aqui exige exceção de capacidade.'
        }
        icon={<CalendarOff className="size-8" />}
      />
    );
  }

  // Agrupamento por equipe é só visual: a ordem já vem do domínio.
  const groups: { team: string; rows: DailySchedule['rows'] }[] = [];
  for (const row of schedule.rows) {
    const team = row.resource.teamName ?? 'Sem equipe';
    const last = groups[groups.length - 1];
    if (last !== undefined && last.team === team) last.rows = [...last.rows, row];
    else groups.push({ team, rows: [row] });
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <section key={group.team} className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)">
            {group.team}
          </h2>

          <div className="flex flex-col divide-y divide-(--color-border-subtle) rounded-(--radius-card) border border-(--color-border-subtle)">
            {group.rows.map((row) => (
              <article key={row.resource.resourceId} className="flex flex-col gap-1 p-3">
                <header className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2">
                    <span className="font-medium">{row.resource.name}</span>
                    <span className="text-sm text-(--color-content-muted)">
                      {row.resource.detail}
                    </span>
                    <button
                      type="button"
                      className="print:hidden flex size-11 items-center justify-center rounded-md text-(--color-content-muted) hover:bg-(--color-surface-muted) hover:text-(--color-content) md:size-8"
                      aria-label={`Imprimir a ficha de ${row.resource.name}`}
                      onClick={() => {
                        onPrintSlip(row.resource.resourceId);
                      }}
                    >
                      <Printer className="size-3.5" aria-hidden="true" />
                    </button>
                  </div>
                  <span
                    className={cn(
                      'tabular text-sm',
                      row.status === 'EXCEEDED' && 'font-semibold text-(--color-state-danger)',
                      row.status === 'AT_LIMIT' && 'text-(--color-state-warn)',
                    )}
                  >
                    {Hours.ofQuarters(row.plannedQuarterHours).format()}
                    {row.capacityQuarterHours === undefined
                      ? ' · sem limite'
                      : ` / ${Hours.ofQuarters(row.capacityQuarterHours).format()}`}
                    {row.status === 'EXCEEDED' && (
                      <>
                        {' '}
                        <AlertTriangle className="inline size-3.5" aria-hidden="true" />
                        <span className="sr-only">{CAPACITY_STATUS_LABEL.EXCEEDED}</span>
                      </>
                    )}
                  </span>
                </header>

                <ul className="flex flex-col gap-1">
                  {row.lines.map((line) => (
                    <li
                      key={line.allocation.id}
                      className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pl-3 text-sm"
                    >
                      <span className="min-w-0">
                        {line.activity.label}
                        <span className="ml-2 text-xs text-(--color-content-muted)">
                          {line.activity.towerName}
                        </span>
                        {/*
                          Exceção **autorizada na época**, não o estado de hoje.

                          Este selo já foi um "Acima da jornada" em vermelho, e ele não é
                          recalculado — `exceedsCapacity` é o registro da autorização
                          (RN-045). Depois de o dia ser ajustado para caber, a linha
                          continuava gritando excesso ao lado de um cabeçalho que já
                          mostrava `7h / 9h`. Quem responde "este dia está acima da
                          jornada" é o total do recurso, logo acima, que é recalculado a
                          cada leitura.
                        */}
                        {line.allocation.exceedsCapacity === true && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-xs text-(--color-content-muted)"
                            title={
                              line.allocation.justification === undefined
                                ? 'Lançada acima da capacidade e autorizada na época.'
                                : `Lançada acima da capacidade e autorizada na época. Motivo: ${line.allocation.justification}`
                            }
                          >
                            <ShieldCheck className="size-3.5" aria-hidden="true" />
                            Exceção autorizada
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="tabular">
                          {Hours.ofQuarters(line.quarterHours).format()}
                        </span>
                        {!readOnly && onMove !== undefined && (
                          <button
                            type="button"
                            className="print:hidden flex size-11 items-center justify-center rounded-md text-(--color-content-muted) hover:bg-(--color-surface-muted) hover:text-(--color-content) md:size-8"
                            aria-label={`Mover ${line.activity.label} de ${row.resource.name} para outro dia`}
                            onClick={() =>
                              onMove({
                                allocationId: line.allocation.id,
                                version: line.allocation.version,
                                resourceName: row.resource.name,
                              })
                            }
                          >
                            <ArrowRightLeft className="size-3.5" aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>
      ))}

      {schedule.idle.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)">
            Sem programação hoje
          </h2>
          <p className="text-sm text-(--color-content-muted)">
            {schedule.idle.map((resource) => resource.name).join(' · ')}
          </p>
        </section>
      )}
    </div>
  );
}
